import { HStack, Spacer, useDisclosure } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import {
  ClockIcon,
  NavigateForwardIcon,
  StopIcon,
  UndoIcon,
  UserIcon,
} from 'components/icons';
import { OptionsMenu } from 'components/OptionsMenu';
import { DeleteOption } from 'components/OptionsMenu/options/DeleteOption';
import { OptionsMenuButton } from 'components/OptionsMenu/OptionsMenuButton';
import { OptionsMenuItem } from 'components/OptionsMenu/OptionsMenuItem';
import { UserAvatar } from 'components/UserAvatar';
import { UserHandle } from 'components/UserHandle';
import React from 'react';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { useI18n } from 'utils/i18n';
import { TodoCardEditorProps } from '.';
import { AssignTodoModal } from './AssignTodoModal';
import { TodoDueDateModal } from './TodoDueDateModal';
import { TodoState } from './__generated__/TodoCard_transitionTodo_Mutation.graphql';
import { TodoOptionsMenu_deleteTodo_Mutation } from './__generated__/TodoOptionsMenu_deleteTodo_Mutation.graphql';
import { TodoOptionsMenu_todo$key } from './__generated__/TodoOptionsMenu_todo.graphql';
import { useFeatureFlag } from 'utils/sessionProvider';

const deleteTodoMutation = graphql`
  mutation TodoOptionsMenu_deleteTodo_Mutation($id: ID!) {
    deleteTodo(input: { id: $id }) {
      todoDeletedId @deleteRecord
    }
  }
`;

const fragment = graphql`
  fragment TodoOptionsMenu_todo on Todo {
    id
    ...AssignTodoModal_todo
    ...TodoDueDateModal_todo
    state
    canTransition {
      value
    }
    canDelete {
      value
    }
    assignee {
      ...UserHandle_member
      ...UserAvatar_member
    }
    dueDate
  }
`;

interface TodoOptionsMenuProps {
  todo: TodoOptionsMenu_todo$key;
  editor?: TodoCardEditorProps;
  onTransition(newState: TodoState): void;
}

export const TodoOptionsMenu = ({
  todo,
  editor,
  onTransition,
}: TodoOptionsMenuProps) => {
  const {
    id,
    state,
    assignee,
    canTransition: { value: canTransition },
    canDelete: { value: canDelete },
    dueDate,
    ..._todo
  } = useFragment(fragment, todo);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isAssignOpen,
    onOpen: onOpenAssign,
    onClose: onCloseAssign,
  } = useDisclosure();
  const {
    isOpen: isDueDateOpen,
    onOpen: onOpenDueDate,
    onClose: onCloseDueDate,
  } = useDisclosure();

  const [deleteTodo, isDeleting] =
    useMutation<TodoOptionsMenu_deleteTodo_Mutation>(deleteTodoMutation);

  const { format } = useI18n();

  const localisedDueDate = dueDate && format(new Date(dueDate), 'PP');
  const customDeletionTextEnabled = useFeatureFlag(
    'FEATURE_CONTEXT_AWARE_DELETION_MODALS',
  );

  const isDone = state === 'CHECKED';
  const isAbandoned = state === 'DISMISSED';

  const onDelete = (onClose: () => void) => {
    if (editor?.editMode) {
      editor.deleteEditorTodo();
      onClose();
    } else {
      deleteTodo({ variables: { id }, onCompleted: onClose });
    }
  };

  const onAbandon = () => {
    onTransition('DISMISSED');
  };

  const onUnabandon = () => {
    onTransition('UNCHECKED');
  };

  if (!canTransition && !canDelete) {
    return null;
  }

  return (
    <>
      <OptionsMenu
        isOpen={isOpen}
        onClose={onClose}
        button={
          <OptionsMenuButton
            onClick={onOpen}
            size="xs"
            colorScheme="gray"
            borderRadius="full"
          />
        }
      >
        <OptionsMenuItem
          isDisabled={!canTransition}
          icon={
            assignee ? <UserAvatar size="xs" member={assignee} /> : <UserIcon />
          }
          onClick={onOpenAssign}
        >
          {assignee ? (
            <HStack w="100%" spacing={1}>
              <span>{t`Assigned to`}</span>
              <UserHandle linkToProfile={false} member={assignee} />
              <Spacer />
              <NavigateForwardIcon />
            </HStack>
          ) : (
            <span>{t`Assign to...`}</span>
          )}
        </OptionsMenuItem>
        <OptionsMenuItem
          isDisabled={!canTransition}
          icon={<ClockIcon />}
          onClick={onOpenDueDate}
        >
          {localisedDueDate ? (
            <HStack>
              <span>{t`Due by ${localisedDueDate}`}</span>
              <Spacer />
              <NavigateForwardIcon />
            </HStack>
          ) : (
            t`Set a due date`
          )}
        </OptionsMenuItem>
        {!isDone && isAbandoned && canTransition && (
          <OptionsMenuItem icon={<UndoIcon />} onClick={onUnabandon}>
            {t`Reopen`}
          </OptionsMenuItem>
        )}
        {!isDone && !isAbandoned && canTransition && (
          <OptionsMenuItem icon={<StopIcon />} onClick={onAbandon}>
            {t`Abandon`}
          </OptionsMenuItem>
        )}
        {(!editor || !!editor.editMode) && (
          <DeleteOption
            isDisabled={!canDelete}
            isDeleting={isDeleting}
            onDelete={onDelete}
            customTitle={
              customDeletionTextEnabled ? t`Delete To-Do?` : undefined
            }
            customBody={
              customDeletionTextEnabled
                ? t`Are you sure you want to delete this to-do? Other members who can view this to-do will no longer have access to it.`
                : undefined
            }
            customConfirm={t`Delete`}
          />
        )}
      </OptionsMenu>
      <AssignTodoModal
        todo={_todo}
        isOpen={isAssignOpen}
        onClose={() => {
          onCloseAssign();
          onClose();
        }}
      />
      <TodoDueDateModal
        isOpen={isDueDateOpen}
        onClose={() => {
          onCloseDueDate();
          onClose();
        }}
        todo={_todo}
      />
    </>
  );
};
